import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Clock, Plus, BookOpen, Users, Calendar, Trash2, 
  GraduationCap, User, Settings, Eye, BarChart3, UserCheck, Edit, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Student Timetable View Component
const StudentTimetableView = ({ getAuthHeader }) => {
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);

  const DAYS_FULL = [
    { id: 'monday', label: 'Monday' },
    { id: 'tuesday', label: 'Tuesday' },
    { id: 'wednesday', label: 'Wednesday' },
    { id: 'thursday', label: 'Thursday' },
    { id: 'friday', label: 'Friday' },
    { id: 'saturday', label: 'Saturday' }
  ];

  useEffect(() => {
    fetchMyTimetable();
  }, []);

  const fetchMyTimetable = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/my-timetable`, { 
        headers: getAuthHeader() 
      });
      // Handle both array and object responses
      const tt = res.data.timetable;
      if (Array.isArray(tt)) {
        setTimetable(tt);
      } else if (tt && typeof tt === 'object') {
        // Convert object format {monday: [...], tuesday: [...]} to array format
        const flattenedTimetable = [];
        Object.entries(tt).forEach(([day, periods]) => {
          if (Array.isArray(periods)) {
            periods.forEach((period, idx) => {
              // Only add if it has a subject (not empty slots)
              if (period.subject && period.subject !== '-') {
                flattenedTimetable.push({
                  day: day.toLowerCase(),
                  subject_name: period.subject,
                  teacher_name: period.teacher,
                  start_time: period.time?.split(' - ')[0] || '',
                  end_time: period.time?.split(' - ')[1] || '',
                  period_number: idx + 1,
                  room: period.room
                });
              }
            });
          }
        });
        setTimetable(flattenedTimetable);
      } else {
        setTimetable([]);
      }
    } catch (error) {
      console.error('Failed to fetch timetable:', error);
      toast.error('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  };

  // Get today's day
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

  // Group timetable by day
  const timetableByDay = DAYS_FULL.reduce((acc, day) => {
    acc[day.id] = timetable.filter(t => t.day === day.id).sort((a, b) => a.period_number - b.period_number);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout title="My Timetable">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Timetable">
      <div className="space-y-6">
        {/* Today's Classes Highlight */}
        <Card className="border-primary">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Today&apos;s Classes ({today.charAt(0).toUpperCase() + today.slice(1)})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {timetableByDay[today]?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {timetableByDay[today].map((slot, idx) => (
                  <div key={idx} className="p-3 border rounded-lg bg-primary/5">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{slot.subject_name}</p>
                        <p className="text-sm text-muted-foreground">Period {slot.period_number}</p>
                      </div>
                      <Badge variant="outline">{slot.start_time} - {slot.end_time}</Badge>
                    </div>
                    <p className="text-sm mt-1"><GraduationCap className="h-3 w-3 inline mr-1" />{slot.teacher_name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No classes scheduled for today</p>
            )}
          </CardContent>
        </Card>

        {/* Full Week Timetable */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Timetable</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {DAYS_FULL.map(day => (
                <div key={day.id} className={`${day.id === today ? 'bg-primary/5 rounded-lg p-3' : ''}`}>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    {day.label}
                    {day.id === today && <Badge className="bg-primary">Today</Badge>}
                  </h3>
                  {timetableByDay[day.id]?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                      {timetableByDay[day.id].map((slot, idx) => (
                        <div key={idx} className="p-2 border rounded text-sm">
                          <p className="font-medium">{slot.subject_name}</p>
                          <p className="text-muted-foreground">{slot.start_time} - {slot.end_time}</p>
                          <p className="text-xs">{slot.teacher_name}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No classes</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const DAYS = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' }
];

const DAYS_FULL = [
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
  { id: 'sunday', label: 'Sunday' }
];

// Helper to get display days from API response or fallback
const getDisplayDays = (apiDays) => {
  if (!apiDays || apiDays.length === 0) {
    return DAYS.filter(d => d.id !== 'saturday' && d.id !== 'sunday');
  }
  return DAYS.filter(d => apiDays.includes(d.id));
};

const TimetableManagementPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('timetable');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [periods, setPeriods] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [workloadData, setWorkloadData] = useState(null);
  const [substitutes, setSubstitutes] = useState([]);
  
  // Selected
  const [selectedClass, setSelectedClass] = useState('');
  const [classTimetable, setClassTimetable] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [teacherTimetable, setTeacherTimetable] = useState(null);
  const [viewMode, setViewMode] = useState('class'); // class or teacher
  
  // Dialogs
  const [periodDialog, setPeriodDialog] = useState(false);
  const [subjectDialog, setSubjectDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [entryDialog, setEntryDialog] = useState(false);
  const [substituteDialog, setSubstituteDialog] = useState(false);
  const [editPeriodDialog, setEditPeriodDialog] = useState(false);
  
  // Forms
  const [periodForm, setPeriodForm] = useState({
    name: '', start_time: '', end_time: '', period_type: 'class', order: 1
  });
  const [subjectForm, setSubjectForm] = useState({
    name: '', code: '', subject_type: 'academic', is_optional: false
  });
  const [assignForm, setAssignForm] = useState({
    class_id: '', subject_id: '', teacher_id: '', periods_per_week: 4
  });
  const [entryForm, setEntryForm] = useState({
    class_id: '', day: '', period_id: '', subject_id: '', teacher_id: '', room: ''
  });
  const [substituteForm, setSubstituteForm] = useState({
    original_entry_id: '', substitute_teacher_id: '', date: '', reason: ''
  });
  const [editingPeriod, setEditingPeriod] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClass && viewMode === 'class') {
      fetchClassTimetable(selectedClass);
      fetchClassSubjects(selectedClass);
    }
  }, [selectedClass, viewMode]);

  useEffect(() => {
    if (selectedTeacher && viewMode === 'teacher') {
      fetchTeacherTimetable(selectedTeacher);
    }
  }, [selectedTeacher, viewMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [periodsRes, subjectsRes, classesRes, teachersRes] = await Promise.all([
        axios.get(`${API_URL}/api/timetable/periods`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/timetable/subjects`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() })
      ]);
      setPeriods(periodsRes.data);
      setSubjects(subjectsRes.data);
      setClasses(classesRes.data);
      // Handle paginated response - teachers API returns {items: [...]}
      const teachersData = teachersRes.data.items || teachersRes.data || [];
      setTeachers(teachersData);
      
      if (classesRes.data.length > 0 && !selectedClass) {
        setSelectedClass(classesRes.data[0].id);
      }
      if (teachersData.length > 0 && !selectedTeacher) {
        setSelectedTeacher(teachersData[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassTimetable = async (classId) => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/class-view/${classId}`, { headers: getAuthHeader() });
      setClassTimetable(res.data);
    } catch (error) {
      console.error('Failed to fetch timetable');
    }
  };

  const fetchTeacherTimetable = async (teacherId) => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/teacher-view/${teacherId}`, { headers: getAuthHeader() });
      setTeacherTimetable(res.data);
    } catch (error) {
      console.error('Failed to fetch teacher timetable');
    }
  };

  const fetchClassSubjects = async (classId) => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/class-subjects/${classId}`, { headers: getAuthHeader() });
      setClassSubjects(res.data);
    } catch (error) {
      setClassSubjects([]);
    }
  };

  const fetchWorkloadReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/workload-report`, { headers: getAuthHeader() });
      setWorkloadData(res.data);
    } catch (error) {
      toast.error('Failed to fetch workload report');
    }
  };

  const fetchSubstitutes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/substitutes`, { headers: getAuthHeader() });
      setSubstitutes(res.data);
    } catch (error) {
      toast.error('Failed to fetch substitutes');
    }
  };

  // Period CRUD
  const createPeriod = async () => {
    try {
      await axios.post(`${API_URL}/api/timetable/periods`, periodForm, { headers: getAuthHeader() });
      toast.success('Period created!');
      setPeriodDialog(false);
      setPeriodForm({ name: '', start_time: '', end_time: '', period_type: 'class', order: periods.length + 1 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create period');
    }
  };

  const updatePeriod = async () => {
    if (!editingPeriod) return;
    try {
      await axios.put(`${API_URL}/api/timetable/periods/${editingPeriod.id}`, periodForm, { headers: getAuthHeader() });
      toast.success('Period updated!');
      setEditPeriodDialog(false);
      setEditingPeriod(null);
      setPeriodForm({ name: '', start_time: '', end_time: '', period_type: 'class', order: 1 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update period');
    }
  };

  const deletePeriod = async (id) => {
    if (!confirm('Delete this period?')) return;
    try {
      await axios.delete(`${API_URL}/api/timetable/periods/${id}`, { headers: getAuthHeader() });
      toast.success('Period deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditPeriod = (period) => {
    setEditingPeriod(period);
    setPeriodForm({
      name: period.name,
      start_time: period.start_time,
      end_time: period.end_time,
      period_type: period.period_type,
      order: period.order
    });
    setEditPeriodDialog(true);
  };

  // Subject CRUD
  const createSubject = async () => {
    try {
      await axios.post(`${API_URL}/api/timetable/subjects`, subjectForm, { headers: getAuthHeader() });
      toast.success('Subject created!');
      setSubjectDialog(false);
      setSubjectForm({ name: '', code: '', subject_type: 'academic', is_optional: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subject');
    }
  };

  const deleteSubject = async (id) => {
    if (!confirm('Delete this subject?')) return;
    try {
      await axios.delete(`${API_URL}/api/timetable/subjects/${id}`, { headers: getAuthHeader() });
      toast.success('Subject deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Assign subject to class
  const assignSubjectToClass = async () => {
    try {
      await axios.post(`${API_URL}/api/timetable/class-subjects`, assignForm, { headers: getAuthHeader() });
      toast.success('Subject assigned!');
      setAssignDialog(false);
      setAssignForm({ class_id: selectedClass, subject_id: '', teacher_id: '', periods_per_week: 4 });
      fetchClassSubjects(selectedClass);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  // Timetable entry
  const createTimetableEntry = async () => {
    try {
      await axios.post(`${API_URL}/api/timetable/entries`, entryForm, { headers: getAuthHeader() });
      toast.success('Entry added!');
      setEntryDialog(false);
      setEntryForm({ class_id: selectedClass, day: '', period_id: '', subject_id: '', teacher_id: '', room: '' });
      if (viewMode === 'class') fetchClassTimetable(selectedClass);
      else fetchTeacherTimetable(selectedTeacher);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const deleteTimetableEntry = async (entryId) => {
    try {
      await axios.delete(`${API_URL}/api/timetable/entries/${entryId}`, { headers: getAuthHeader() });
      toast.success('Entry removed');
      if (viewMode === 'class') fetchClassTimetable(selectedClass);
      else fetchTeacherTimetable(selectedTeacher);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEntryDialog = (day, periodId) => {
    setEntryForm({
      class_id: selectedClass,
      day: day,
      period_id: periodId,
      subject_id: '',
      teacher_id: '',
      room: ''
    });
    setEntryDialog(true);
  };

  // Substitute
  const assignSubstitute = async () => {
    try {
      await axios.post(`${API_URL}/api/timetable/substitute`, substituteForm, { headers: getAuthHeader() });
      toast.success('Substitute assigned!');
      setSubstituteDialog(false);
      setSubstituteForm({ original_entry_id: '', substitute_teacher_id: '', date: '', reason: '' });
      fetchSubstitutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign substitute');
    }
  };

  const cancelSubstitute = async (id) => {
    if (!confirm('Cancel this substitute assignment?')) return;
    try {
      await axios.delete(`${API_URL}/api/timetable/substitute/${id}`, { headers: getAuthHeader() });
      toast.success('Substitute cancelled');
      fetchSubstitutes();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const isAdmin = user?.role === 'school_admin' || user?.role === 'super_admin';
  const isTeacher = user?.role === 'teacher' || user?.role === 'staff';

  return (
    <DashboardLayout title={isTeacher ? "My Timetable" : "Timetable Management"}>
      <div className="space-y-6" data-testid="timetable-management-page">
        {isAdmin ? (
          // Admin gets full tabs
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'workload') fetchWorkloadReport(); if (v === 'substitute') fetchSubstitutes(); }}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="timetable" data-testid="timetable-tab"><Calendar className="h-4 w-4 mr-1" /> Timetable</TabsTrigger>
              <TabsTrigger value="setup" data-testid="setup-tab"><Settings className="h-4 w-4 mr-1" /> Setup</TabsTrigger>
              <TabsTrigger value="workload" data-testid="workload-tab"><BarChart3 className="h-4 w-4 mr-1" /> Workload</TabsTrigger>
              <TabsTrigger value="substitute" data-testid="substitute-tab"><UserCheck className="h-4 w-4 mr-1" /> Substitute</TabsTrigger>
            </TabsList>

          {/* Timetable Tab */}
          <TabsContent value="timetable" className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Select value={viewMode} onValueChange={(v) => setViewMode(v)}>
                  <SelectTrigger className="w-32" data-testid="view-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="class">Class View</SelectItem>
                    <SelectItem value="teacher">Teacher View</SelectItem>
                  </SelectContent>
                </Select>
                
                {viewMode === 'class' ? (
                  <Select value={selectedClass} onValueChange={setSelectedClass}>
                    <SelectTrigger className="w-48" data-testid="class-select">
                      <SelectValue placeholder="Select Class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                    <SelectTrigger className="w-48" data-testid="teacher-select">
                      <SelectValue placeholder="Select Teacher" />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <Badge variant="outline" className="text-base px-3 py-1">
                {viewMode === 'class' ? classTimetable?.class_name : teacherTimetable?.teacher_name || 'Select'}
              </Badge>
            </div>

            {viewMode === 'class' && classTimetable && (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full min-w-[800px]" data-testid="class-timetable-grid">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="p-2 text-left text-sm border-r w-24">Period</th>
                        {getDisplayDays(classTimetable.days).map(d => (
                          <th key={d.id} className="p-2 text-center text-sm border-r">{d.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {classTimetable.periods.map(period => (
                        <tr key={period.id} className={`border-t ${period.period_type === 'break' ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                          <td className="p-2 border-r">
                            <div className="text-sm font-medium">{period.name}</div>
                            <div className="text-xs text-muted-foreground">{period.start_time}-{period.end_time}</div>
                          </td>
                          {getDisplayDays(classTimetable.days).map(day => {
                            const cell = classTimetable.timetable[day.id]?.find(c => c.period_id === period.id);
                            if (period.period_type === 'break') {
                              return (
                                <td key={day.id} className="p-2 text-center border-r bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm">
                                  Break
                                </td>
                              );
                            }
                            return (
                              <td key={day.id} className="p-2 border-r min-w-[120px]">
                                {cell?.subject ? (
                                  <div className="bg-blue-50 dark:bg-blue-900/30 p-2 rounded text-center relative group">
                                    <p className="font-medium text-sm text-blue-900 dark:text-blue-200">{cell.subject}</p>
                                    <p className="text-xs text-blue-600 dark:text-blue-400">{cell.teacher}</p>
                                    {cell.room && <p className="text-xs text-muted-foreground">Room: {cell.room}</p>}
                                    {isAdmin && (
                                      <button 
                                        onClick={() => deleteTimetableEntry(cell.entry_id)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                        data-testid={`delete-entry-${day.id}-${period.id}`}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ) : isAdmin ? (
                                  <button
                                    onClick={() => openEntryDialog(day.id, period.id)}
                                    className="w-full h-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded hover:border-primary hover:bg-primary/5 flex items-center justify-center text-muted-foreground text-sm"
                                    data-testid={`add-entry-${day.id}-${period.id}`}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <div className="w-full h-12 flex items-center justify-center text-muted-foreground text-sm">-</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {viewMode === 'teacher' && teacherTimetable && (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full min-w-[800px]" data-testid="teacher-timetable-grid">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="p-2 text-left text-sm border-r w-24">Period</th>
                        {getDisplayDays(teacherTimetable.days).map(d => (
                          <th key={d.id} className="p-2 text-center text-sm border-r">{d.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {teacherTimetable.periods.map((period, idx) => (
                        <tr key={idx} className={`border-t ${period.period_type === 'break' ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                          <td className="p-2 border-r">
                            <div className="text-sm font-medium">{period.name}</div>
                            <div className="text-xs text-muted-foreground">{period.start_time}-{period.end_time}</div>
                          </td>
                          {getDisplayDays(teacherTimetable.days).map(day => {
                            const cell = teacherTimetable.timetable[day.id]?.[idx];
                            if (cell?.is_break) {
                              return (
                                <td key={day.id} className="p-2 text-center border-r bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm">
                                  Break
                                </td>
                              );
                            }
                            return (
                              <td key={day.id} className="p-2 border-r min-w-[120px]">
                                {cell?.subject ? (
                                  <div className="bg-green-50 dark:bg-green-900/30 p-2 rounded text-center">
                                    <p className="font-medium text-sm text-green-900 dark:text-green-200">{cell.subject}</p>
                                    <p className="text-xs text-green-600 dark:text-green-400">{cell.class}</p>
                                    {cell.room && <p className="text-xs text-muted-foreground">Room: {cell.room}</p>}
                                  </div>
                                ) : (
                                  <div className="p-2 text-center text-muted-foreground text-sm">Free</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
            
            {!classTimetable && !teacherTimetable && periods.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center">
                  <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">First, set up Periods in the Setup tab</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Setup Tab */}
          <TabsContent value="setup" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Periods */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-5 w-5" /> Periods
                    </CardTitle>
                    {isAdmin && (
                      <Button size="sm" onClick={() => setPeriodDialog(true)} data-testid="add-period-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    )}
                  </div>
                  <CardDescription>Define school periods and breaks</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {periods.map((p, i) => (
                      <div key={p.id} className="flex items-center justify-between p-2 border rounded-lg" data-testid={`period-${i}`}>
                        <div className="flex items-center gap-3">
                          <Badge variant={p.period_type === 'break' ? 'secondary' : 'outline'}>{i + 1}</Badge>
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            <p className="text-xs text-muted-foreground">{p.start_time} - {p.end_time} • {p.period_type}</p>
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditPeriod(p)}>
                              <Edit className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deletePeriod(p.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {periods.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No periods defined</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Subjects */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="h-5 w-5" /> Subjects
                    </CardTitle>
                    {isAdmin && (
                      <Button size="sm" onClick={() => setSubjectDialog(true)} data-testid="add-subject-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    )}
                  </div>
                  <CardDescription>Define subjects for the school</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {subjects.map(s => (
                      <div key={s.id} className="flex items-center justify-between p-2 border rounded-lg" data-testid={`subject-${s.code}`}>
                        <div>
                          <p className="font-medium text-sm">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{s.code} • {s.subject_type}</p>
                        </div>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => deleteSubject(s.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {subjects.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No subjects defined</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Class-Subject Mapping */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Class-Subject-Teacher Mapping</CardTitle>
                    <CardDescription>Assign subjects and teachers to classes</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select Class" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isAdmin && (
                      <Button size="sm" onClick={() => { setAssignForm({ ...assignForm, class_id: selectedClass }); setAssignDialog(true); }} data-testid="assign-subject-btn">
                        <Plus className="h-4 w-4 mr-1" /> Assign Subject
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 text-left text-sm">Subject</th>
                        <th className="p-2 text-left text-sm">Code</th>
                        <th className="p-2 text-left text-sm">Teacher</th>
                        <th className="p-2 text-center text-sm">Periods/Week</th>
                        {isAdmin && <th className="p-2 text-center text-sm">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {classSubjects.map(cs => (
                        <tr key={cs.id} className="border-t">
                          <td className="p-2 font-medium">{cs.subject_name}</td>
                          <td className="p-2 text-sm text-muted-foreground">{cs.subject_code}</td>
                          <td className="p-2 text-sm">{cs.teacher_name}</td>
                          <td className="p-2 text-center">{cs.periods_per_week}</td>
                          {isAdmin && (
                            <td className="p-2 text-center">
                              <Button size="sm" variant="ghost" onClick={() => axios.delete(`${API_URL}/api/timetable/class-subjects/${cs.id}`, { headers: getAuthHeader() }).then(() => { toast.success('Removed'); fetchClassSubjects(selectedClass); })}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {classSubjects.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No subjects assigned to this class</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workload Tab */}
          <TabsContent value="workload" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" /> Teacher Workload Report
                    </CardTitle>
                    <CardDescription>View periods per week for each teacher</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchWorkloadReport} data-testid="refresh-workload-btn">
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {workloadData && (
                  <>
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{workloadData.summary.total_teachers}</p>
                        <p className="text-sm text-muted-foreground">Total Teachers</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-600">{workloadData.summary.teachers_with_load}</p>
                        <p className="text-sm text-muted-foreground">With Classes</p>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{workloadData.summary.teachers_without_load}</p>
                        <p className="text-sm text-muted-foreground">Without Classes</p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{workloadData.summary.avg_periods_per_teacher?.toFixed(1)}</p>
                        <p className="text-sm text-muted-foreground">Avg Periods/Week</p>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full" data-testid="workload-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-3 text-left text-sm">Teacher</th>
                            <th className="p-3 text-left text-sm">Designation</th>
                            <th className="p-3 text-center text-sm">Total Periods</th>
                            <th className="p-3 text-left text-sm">Subject Breakdown</th>
                            <th className="p-3 text-left text-sm">Class Breakdown</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workloadData.workload.map(w => (
                            <tr key={w.teacher_id} className="border-t">
                              <td className="p-3 font-medium">{w.teacher_name}</td>
                              <td className="p-3 text-sm text-muted-foreground">{w.designation || '-'}</td>
                              <td className="p-3 text-center">
                                <Badge variant={w.total_periods_per_week > 25 ? 'destructive' : w.total_periods_per_week > 0 ? 'default' : 'secondary'}>
                                  {w.total_periods_per_week}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(w.subject_breakdown).map(([subj, count]) => (
                                    <Badge key={subj} variant="outline" className="text-xs">{subj}: {count}</Badge>
                                  ))}
                                  {Object.keys(w.subject_breakdown).length === 0 && <span className="text-muted-foreground text-sm">-</span>}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(w.class_breakdown).map(([cls, count]) => (
                                    <Badge key={cls} variant="outline" className="text-xs">{cls}: {count}</Badge>
                                  ))}
                                  {Object.keys(w.class_breakdown).length === 0 && <span className="text-muted-foreground text-sm">-</span>}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {workloadData.workload.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No workload data</p>
                      )}
                    </div>
                  </>
                )}
                {!workloadData && (
                  <p className="text-center text-muted-foreground py-8">Click Refresh to load workload report</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Substitute Tab */}
          <TabsContent value="substitute" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <UserCheck className="h-5 w-5" /> Substitute Teacher Management
                    </CardTitle>
                    <CardDescription>Assign substitute teachers for absent staff</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchSubstitutes}>
                      <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                    </Button>
                    {isAdmin && (
                      <Button size="sm" onClick={() => setSubstituteDialog(true)} data-testid="add-substitute-btn">
                        <Plus className="h-4 w-4 mr-1" /> Assign Substitute
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full" data-testid="substitute-table">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-3 text-left text-sm">Date</th>
                        <th className="p-3 text-left text-sm">Day</th>
                        <th className="p-3 text-left text-sm">Period</th>
                        <th className="p-3 text-left text-sm">Class</th>
                        <th className="p-3 text-left text-sm">Subject</th>
                        <th className="p-3 text-left text-sm">Original Teacher</th>
                        <th className="p-3 text-left text-sm">Substitute</th>
                        <th className="p-3 text-left text-sm">Reason</th>
                        {isAdmin && <th className="p-3 text-center text-sm">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {substitutes.map(sub => (
                        <tr key={sub.id} className="border-t">
                          <td className="p-3">{sub.date}</td>
                          <td className="p-3 capitalize">{sub.day}</td>
                          <td className="p-3">{sub.period_name}</td>
                          <td className="p-3">{sub.class_name}</td>
                          <td className="p-3">{sub.subject_name}</td>
                          <td className="p-3 text-red-600">{sub.original_teacher_name}</td>
                          <td className="p-3 text-green-600 font-medium">{sub.substitute_teacher_name}</td>
                          <td className="p-3 text-muted-foreground text-sm">{sub.reason || '-'}</td>
                          {isAdmin && (
                            <td className="p-3 text-center">
                              <Button size="sm" variant="ghost" onClick={() => cancelSubstitute(sub.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {substitutes.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No substitute assignments</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        ) : (
          // Teacher/Staff gets view-only timetable without tabs
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                My Weekly Schedule
              </CardTitle>
              <CardDescription>
                View your class schedule. Select a class to see the timetable.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <Select value={viewMode} onValueChange={(v) => setViewMode(v)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="class">Class View</SelectItem>
                      <SelectItem value="teacher">Teacher View</SelectItem>
                    </SelectContent>
                  </Select>
                  {viewMode === 'class' ? (
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select Class" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select Teacher" />
                      </SelectTrigger>
                      <SelectContent>
                        {teachers.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Timetable Grid for Teachers - View Only */}
              {viewMode === 'class' && classTimetable && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr>
                        <th className="p-2 border bg-muted text-left">Period</th>
                        {DAYS_FULL.map(d => (
                          <th key={d.id} className="p-2 border bg-muted text-center">{d.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {classTimetable.periods.map(period => (
                        <tr key={period.id}>
                          <td className="p-2 border font-medium whitespace-nowrap">
                            <div>{period.name}</div>
                            <div className="text-xs text-muted-foreground">{period.start_time} - {period.end_time}</div>
                          </td>
                          {DAYS_FULL.map(day => {
                            const cell = classTimetable.timetable[day.id]?.find(c => c.period_id === period.id);
                            return (
                              <td key={day.id} className="p-2 border text-center">
                                {cell?.is_break || period.is_break ? (
                                  <span className="text-muted-foreground">Break</span>
                                ) : cell?.subject ? (
                                  <div>
                                    <div className="font-medium text-primary">{cell.subject}</div>
                                    <div className="text-xs text-muted-foreground">{cell.teacher}</div>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewMode === 'teacher' && teacherTimetable && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr>
                        <th className="p-2 border bg-muted text-left">Period</th>
                        {DAYS_FULL.map(d => (
                          <th key={d.id} className="p-2 border bg-muted text-center">{d.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {teacherTimetable.periods.map((period, idx) => (
                        <tr key={period.id || idx}>
                          <td className="p-2 border font-medium whitespace-nowrap">
                            <div>{period.name || period.period_name}</div>
                            <div className="text-xs text-muted-foreground">{period.start_time} - {period.end_time}</div>
                          </td>
                          {DAYS_FULL.map(day => {
                            const daySchedule = teacherTimetable.timetable[day.id] || [];
                            const cell = daySchedule[idx];
                            return (
                              <td key={day.id} className="p-2 border text-center">
                                {cell?.is_break ? (
                                  <span className="text-amber-600 font-medium">Break</span>
                                ) : cell?.subject ? (
                                  <div>
                                    <div className="font-medium text-primary">{cell.subject}</div>
                                    <div className="text-xs text-muted-foreground">{cell.class}</div>
                                    {cell.room && <div className="text-xs text-blue-500">Room: {cell.room}</div>}
                                  </div>
                                ) : (
                                  <span className="text-green-600 font-medium">Free</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!classTimetable && !teacherTimetable && (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Select a class or teacher to view the timetable</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Period Dialog */}
      <Dialog open={periodDialog} onOpenChange={setPeriodDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Period</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Period Name *</Label>
              <Input value={periodForm.name} onChange={e => setPeriodForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., 1st Period" data-testid="period-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time *</Label>
                <Input type="time" value={periodForm.start_time} onChange={e => setPeriodForm(p => ({ ...p, start_time: e.target.value }))} data-testid="period-start-input" />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input type="time" value={periodForm.end_time} onChange={e => setPeriodForm(p => ({ ...p, end_time: e.target.value }))} data-testid="period-end-input" />
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={periodForm.period_type} onValueChange={v => setPeriodForm(p => ({ ...p, period_type: v }))}>
                <SelectTrigger data-testid="period-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="class">Class</SelectItem>
                  <SelectItem value="break">Break/Recess</SelectItem>
                  <SelectItem value="assembly">Assembly</SelectItem>
                  <SelectItem value="sports">Sports/PT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPeriodDialog(false)}>Cancel</Button>
            <Button onClick={createPeriod} data-testid="create-period-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Period Dialog */}
      <Dialog open={editPeriodDialog} onOpenChange={setEditPeriodDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Period</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Period Name *</Label>
              <Input value={periodForm.name} onChange={e => setPeriodForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., 1st Period" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time *</Label>
                <Input type="time" value={periodForm.start_time} onChange={e => setPeriodForm(p => ({ ...p, start_time: e.target.value }))} />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input type="time" value={periodForm.end_time} onChange={e => setPeriodForm(p => ({ ...p, end_time: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={periodForm.period_type} onValueChange={v => setPeriodForm(p => ({ ...p, period_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="class">Class</SelectItem>
                  <SelectItem value="break">Break/Recess</SelectItem>
                  <SelectItem value="assembly">Assembly</SelectItem>
                  <SelectItem value="sports">Sports/PT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPeriodDialog(false)}>Cancel</Button>
            <Button onClick={updatePeriod}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subject Dialog */}
      <Dialog open={subjectDialog} onOpenChange={setSubjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Subject</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject Name *</Label>
              <Input value={subjectForm.name} onChange={e => setSubjectForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Mathematics" data-testid="subject-name-input" />
            </div>
            <div>
              <Label>Subject Code *</Label>
              <Input value={subjectForm.code} onChange={e => setSubjectForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="e.g., MATH" data-testid="subject-code-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={subjectForm.subject_type} onValueChange={v => setSubjectForm(p => ({ ...p, subject_type: v }))}>
                <SelectTrigger data-testid="subject-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="academic">Academic</SelectItem>
                  <SelectItem value="elective">Elective</SelectItem>
                  <SelectItem value="extracurricular">Extracurricular</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubjectDialog(false)}>Cancel</Button>
            <Button onClick={createSubject} data-testid="create-subject-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Subject Dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Subject to Class</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject *</Label>
              <Select value={assignForm.subject_id} onValueChange={v => setAssignForm(p => ({ ...p, subject_id: v }))}>
                <SelectTrigger data-testid="assign-subject-select"><SelectValue placeholder="Select Subject" /></SelectTrigger>
                <SelectContent>
                  {subjects.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teacher</Label>
              <Select value={assignForm.teacher_id} onValueChange={v => setAssignForm(p => ({ ...p, teacher_id: v }))}>
                <SelectTrigger data-testid="assign-teacher-select"><SelectValue placeholder="Select Teacher" /></SelectTrigger>
                <SelectContent>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Periods per Week</Label>
              <Input type="number" value={assignForm.periods_per_week} onChange={e => setAssignForm(p => ({ ...p, periods_per_week: parseInt(e.target.value) }))} data-testid="assign-periods-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(false)}>Cancel</Button>
            <Button onClick={assignSubjectToClass} data-testid="assign-btn">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timetable Entry Dialog */}
      <Dialog open={entryDialog} onOpenChange={setEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Timetable Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-2 bg-muted rounded text-sm">
              <strong>{DAYS_FULL.find(d => d.id === entryForm.day)?.label}</strong> • {periods.find(p => p.id === entryForm.period_id)?.name}
            </div>
            <div>
              <Label>Subject *</Label>
              <Select value={entryForm.subject_id} onValueChange={v => setEntryForm(p => ({ ...p, subject_id: v }))}>
                <SelectTrigger data-testid="entry-subject-select"><SelectValue placeholder="Select Subject" /></SelectTrigger>
                <SelectContent>
                  {classSubjects.length > 0 ? classSubjects.map(cs => (
                    <SelectItem key={cs.subject_id} value={cs.subject_id}>{cs.subject_name}</SelectItem>
                  )) : subjects.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teacher *</Label>
              <Select value={entryForm.teacher_id} onValueChange={v => setEntryForm(p => ({ ...p, teacher_id: v }))}>
                <SelectTrigger data-testid="entry-teacher-select"><SelectValue placeholder="Select Teacher" /></SelectTrigger>
                <SelectContent>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Room (Optional)</Label>
              <Input value={entryForm.room} onChange={e => setEntryForm(p => ({ ...p, room: e.target.value }))} placeholder="e.g., Room 101" data-testid="entry-room-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryDialog(false)}>Cancel</Button>
            <Button onClick={createTimetableEntry} data-testid="create-entry-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Substitute Dialog */}
      <Dialog open={substituteDialog} onOpenChange={setSubstituteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Substitute Teacher</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={substituteForm.date} onChange={e => setSubstituteForm(p => ({ ...p, date: e.target.value }))} data-testid="substitute-date-input" />
            </div>
            <div>
              <Label>Select Class</Label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger><SelectValue placeholder="Select Class" /></SelectTrigger>
                <SelectContent>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Timetable Entry (Period) *</Label>
              <Select value={substituteForm.original_entry_id} onValueChange={v => setSubstituteForm(p => ({ ...p, original_entry_id: v }))}>
                <SelectTrigger data-testid="substitute-entry-select"><SelectValue placeholder="Select Period" /></SelectTrigger>
                <SelectContent>
                  {classTimetable && Object.entries(classTimetable.timetable).flatMap(([day, cells]) => 
                    cells.filter(c => c.entry_id && c.subject).map(c => (
                      <SelectItem key={c.entry_id} value={c.entry_id}>
                        {DAYS_FULL.find(d => d.id === day)?.label} - {c.period_name} - {c.subject} ({c.teacher})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Substitute Teacher *</Label>
              <Select value={substituteForm.substitute_teacher_id} onValueChange={v => setSubstituteForm(p => ({ ...p, substitute_teacher_id: v }))}>
                <SelectTrigger data-testid="substitute-teacher-select"><SelectValue placeholder="Select Teacher" /></SelectTrigger>
                <SelectContent>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason (Optional)</Label>
              <Input value={substituteForm.reason} onChange={e => setSubstituteForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g., Sick Leave" data-testid="substitute-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubstituteDialog(false)}>Cancel</Button>
            <Button onClick={assignSubstitute} data-testid="assign-substitute-btn-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Main Component with Role-based Routing
const TimetablePageWrapper = () => {
  const { getAuthHeader, user } = useAuth();
  
  // Student gets simplified view
  if (user?.role === 'student') {
    return <StudentTimetableView getAuthHeader={getAuthHeader} />;
  }
  
  // Admin/Teacher gets full view
  return <TimetableManagementPage />;
};

export default TimetablePageWrapper;
